import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { api, useAuth, loginPathFor } from '@onvice/portal-shell-api';

interface Props {
  children: ReactNode;
  moduleSlug: string;
}

interface ModuleAccess {
  slug: string;
  level: 'read' | 'write' | 'delete';
}

/**
 * Prueft Anwendungs-Sichtbarkeit (Achse 1) anhand von /portal/my-access.
 * Ohne Eintrag → redirect /. Backend verteidigt zusaetzlich, das hier ist
 * UX (nicht den Inhalt der Anwendung anzeigen, wenn der User keinen Zugriff hat).
 */
export default function ModuleGuard({ children, moduleSlug }: Props) {
  const location = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [accessLoading, setAccessLoading] = useState(true);
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isAuthenticated) { setAccessLoading(false); return; }
    api.get<{ success: boolean; data: { modules: ModuleAccess[] } }>('/portal/my-access')
      .then((r) => {
        const ok = (r.data.modules ?? []).some((m) => m.slug === moduleSlug);
        setAllowed(ok);
      })
      .catch(() => setAllowed(false))
      .finally(() => setAccessLoading(false));
  }, [moduleSlug, isAuthenticated]);

  if (authLoading || accessLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Ziel als ?next= mitgeben (Holger-Fund 2026-09-17, siehe ProtectedRoute).
  if (!isAuthenticated) return <Navigate to={loginPathFor(`${location.pathname}${location.search}${location.hash}`)} replace />;
  if (allowed === false) return <Navigate to="/" replace />;
  return <>{children}</>;
}
